<!-- Copyright (c) 2026 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="manage-main">
    <div class="left-menu">
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>

    <div class="manage-workspace">
      <div v-if="activePage == 'coefficientSettings'" class="page-content coefficient-page">
        <div class="content-header">
          <div>Coefficient Settings</div>
        </div>

        <div class="coefficient-top">
          <BoxContainer class="price-panel" title="Price Coefficient">
            <div class="table-wrap cipl-table-wrap">
              <table class="data-table price-grid">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Job Type</th>
                    <th>Price Coefficient</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in priceCoefficients" :key="row['#']">
                    <td>{{ row['#'] }}</td>
                    <td>{{ row['Job Type'] }}</td>
                    <td>
                      <Input
                        placeholder=""
                        :inputValue.sync="row['Price Coefficient']"
                        :isDisabled="!editMode.coefficientSettings"
                        validator="positiveDecimal,11,5"
                        @blur="formatPriceCoefficient(row)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <RadioButtonSet
              title="Use Price Coeff for Driver Inc?"
              :buttons="driverCoeffOptions"
              labelKey="label"
              :selected.sync="selectedDriverCoeffMode"
              :isHorizontal="true"
              :showBox="false"
              :isDisabled="!editMode.coefficientSettings"
            />
          </BoxContainer>

          <BoxContainer class="movement-panel" title="Movement Type Coefficient">
            <div class="table-wrap cipl-table-wrap">
              <table class="data-table incentive-grid coefficient-grid">
                <thead>
                  <tr>
                    <th>Job Type</th>

                    <th v-for="movement in movementTypes" :key="movement">
                      {{ movement }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-if="coefficientLoading" class="table-message-row">
                    <td :colspan="movementTypes.length + 1" class="table-message-cell">
                      Loading Movement Type Coefficient data . . .
                    </td>
                  </tr>

                  <tr v-else-if="movementCoefficients.length == 0" class="table-message-row">
                    <td :colspan="movementTypes.length + 1" class="table-message-cell"> No items </td>
                  </tr>

                  <tr v-else v-for="row in movementCoefficients" :key="row.JobTypeCode">
                    <td>{{ row['Job Type'] }}</td>

                    <td v-for="movement in movementTypes" :key="movement">
                      <Input
                        placeholder=""
                        :inputValue.sync="row[movement].value"
                        :isDisabled="row[movement].disabled || !editMode.coefficientSettings"
                        validator="positiveDecimal,11,5"
                        @blur="formatCoefficientCell(row[movement])"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </BoxContainer>
        </div>

        <div class="bottom-actions">
          <template v-if="!editMode.coefficientSettings">
            <button :disabled="coefficientLoading" @click="editCoefficientSettings"> Edit </button>
          </template>

          <template v-else>
            <button @click="saveCoefficientSettings"> Save Edit </button>
            <button @click="cancelCoefficientSettings"> Cancel </button>
          </template>
        </div>
      </div>

      <div v-if="activePage == 'driverIncentive'" class="page-content">
        <div class="content-header">
          <div>Driver Incentive</div>
        </div>

        <div v-if="driverTabIndex != 0" class="top-selector">
          <InputQuery
            placeholder="Commission Code"
            :items="commissionList"
            itemKey="Code - Description"
            :selectedObject.sync="selectedCommission"
            :isDisabled="editMode.revenueMovement || editMode.nonRevenue || editMode.fixedZone || editMode.incremental"
            @selected="commissionSelected"
          />
        </div>

        <div v-else class="top-selector driver-commission-selector">
          <!-- for Edit -->
          <InputQuery
            v-if="formMode.driverCommission != 'new'"
            key="driver-commission-existing"
            class="commission-code-input"
            placeholder="Commission Code"
            :items="driverCommissionList"
            itemKey="Com Code"
            :selectedObject.sync="selectedDriverCommission"
            :isDisabled="editMode.driverCommission"
            @selected="driverCommissionSelected"
          />

          <!-- for New -->
          <InputQuery
            v-else
            key="driver-commission-new"
            class="commission-code-input"
            placeholder="Commission Code"
            :items="[]"
            itemKey="Com Code"
            :search.sync="driverCommissionCode"
            :allowFreeText="true"
            validator="text,10,uppercase,alphanumeric"
          />
          <Input
            class="commission-description-input"
            placeholder="Description"
            :inputValue.sync="driverCommissionDescription"
            :isDisabled="!editMode.driverCommission"
          />
        </div>
        <Tab class="driver-incentive-tab" :tabs="driverIncentiveTabs" :tabIndex.sync="driverTabIndex">
          <div slot="Driver Commission Structure" class="driver-tab-page">
            <BoxContainer
              v-if="coefficientSettingsDetail.ComHeader"
              class="coefficient-settings-detail coefficient-settings-bottom"
              :title="
                formMode.driverCommission == 'new' ? 'New Driver Commission Structure'
                : formMode.driverCommission == 'edit' ? 'Edit Driver Commission Structure'
                : 'Driver Commission Structure'
              "
            >
              <div class="field-grid three">
                <Input
                  placeholder="Driver Basic Pay (RM)"
                  :inputValue.sync="coefficientSettingsDetail.ComHeader.Basic_Pay1"
                  validator="positiveDecimal,19,2"
                  :isDisabled="!editMode.driverCommission"
                />
                <Input
                  placeholder="Attendant Basic Pay (RM)"
                  :inputValue.sync="coefficientSettingsDetail.ComHeader.Basic_Pay2"
                  validator="positiveDecimal,19,2"
                  :isDisabled="!editMode.driverCommission"
                />
              </div>

              <BoxContainer title="Monthly Bonus Incentive">
                <div class="formula-row">
                  <span>If comm. RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.Range"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span>every RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.IncEvery"
                    validator="positiveDecimal,11,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <Input
                    class="formula-input"
                    placeholder="Driver"
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.Driver"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.monthlyBonus.Qlf }} </span>
                  <Input
                    class="formula-input"
                    placeholder="Attendant"
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.Attendant"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.monthlyBonus.Qlf }} </span>
                  <Dropdown
                    class="formula-dropdown"
                    placeholder="Type"
                    :items="amountTypeOptions"
                    itemKey="label"
                    :selectedObject.sync="selectedMonthlyType"
                    @selected="commissionTypeSelected('monthlyBonus', $event)"
                    :isDisabled="!editMode.driverCommission"
                  />
                </div>
              </BoxContainer>

              <BoxContainer title="Reserve (Tabung)">
                <div class="formula-row">
                  <span>If comm. RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.reserve.Range"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span>every RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.reserve.IncEvery"
                    validator="positiveDecimal,11,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <Input
                    class="formula-input"
                    placeholder="Driver"
                    :inputValue.sync="coefficientSettingsDetail.reserve.Driver"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.reserve.Qlf }} </span>
                  <Input
                    class="formula-input"
                    placeholder="Attendant"
                    :inputValue.sync="coefficientSettingsDetail.reserve.Attendant"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.reserve.Qlf }} </span>
                  <Dropdown
                    placeholder="Type"
                    :items="amountTypeOptions"
                    itemKey="label"
                    :selectedObject.sync="selectedReserveType"
                    @selected="commissionTypeSelected('reserve', $event)"
                    :isDisabled="!editMode.driverCommission"
                  />
                </div>
              </BoxContainer>
            </BoxContainer>
            <div v-else class="commission-loading"> Loading commission details . . . </div>

            <div class="bottom-actions">
              <template v-if="!editMode.driverCommission">
                <button @click="newDriverCommission"> New </button>
                <button :disabled="!coefficientSettingsDetail.ComHeader" @click="editDriverCommission"> Edit </button>
                <button :disabled="!coefficientSettingsDetail.ComHeader" @click="deleteDriverCommission"> Delete </button>
              </template>

              <template v-else>
                <button @click="saveDriverCommission">
                  {{ formMode.driverCommission == 'new' ? 'Save New' : 'Save Edit' }}
                </button>

                <button @click="cancelDriverCommission"> Cancel </button>
              </template>
            </div>
          </div>

          <div slot="Revenue Movement Incentive" class="driver-tab-page">
            <BoxContainer class="fill-panel" title="Revenue Movement Incentive Coefficient">
              <div class="table-wrap cipl-table-wrap">
                <table class="data-table incentive-grid">
                  <thead>
                    <tr>
                      <th>Job Type</th>
                      <th v-for="movement in movementTypes" :key="movement">{{ movement }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="tableLoading" class="table-message-row">
                      <td :colspan="movementTypes.length + 1" class="table-message-cell">
                        Loading Revenue Movement Incentive Coefficient data . . .
                      </td>
                    </tr>

                    <tr v-else-if="revMvtIncDet.length == 0" class="table-message-row">
                      <td :colspan="movementTypes.length + 1" class="table-message-cell"> No items </td>
                    </tr>

                    <tr v-else v-for="row in revMvtIncDet" :key="row['Job Type']">
                      <td>{{ row['Job Type'] }}</td>

                      <td v-for="movement in movementTypes" :key="movement">
                        <div class="amount-cell">
                          <Input
                            placeholder=""
                            :inputValue.sync="row[movement].value"
                            :isDisabled="row[movement].disabled || !editMode.revenueMovement"
                            :validator="getAmountValidator(row[movement])"
                            @blur="formatAmountCell(row[movement])"
                          />

                          <Dropdown
                            :class="['type-dropdown', { 'dropdown-open-left': isLastMovement(movement) }]"
                            placeholder=""
                            :items="amountTypeOptions"
                            itemKey="label"
                            :selectedObject.sync="row[movement].selectedType"
                            :isDisabled="row[movement].disabled || !editMode.revenueMovement"
                            @selected="amountTypeSelected(row[movement], $event)"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="diversion-row">
                <label v-for="d in diversionDet" :key="d.key">
                  <span>{{ d.key }}</span>

                  <Input
                    placeholder=""
                    :inputValue.sync="d.value"
                    :isDisabled="tableLoading || !editMode.revenueMovement"
                    :validator="getAmountValidator(d)"
                    @blur="formatAmountCell(d)"
                  />

                  <Dropdown
                    placeholder=""
                    :items="amountTypeOptions"
                    itemKey="label"
                    :selectedObject.sync="d.selectedType"
                    :isDisabled="tableLoading || !editMode.revenueMovement"
                    @selected="amountTypeSelected(d, $event)"
                  />
                </label>
              </div>
            </BoxContainer>

            <div class="bottom-actions">
              <template v-if="!editMode.revenueMovement">
                <button :disabled="tableLoading || !selectedCommission" @click="editRevenueMovement"> Edit </button>
              </template>

              <template v-else>
                <button @click="saveRevenueMovement">
                  {{ formMode.revenueMovement == 'new' ? 'Save New' : 'Save Edit' }}
                </button>

                <button @click="cancelRevenueMovement"> Cancel </button>
              </template>
            </div>
          </div>

          <div slot="Non-Rev Mvt Inc (Fix)" class="driver-tab-page split-view">
            <div class="list-side">
              <div class="tablebox-area">
                <TableBox
                  v-if="!tableLoading"
                  title="Non-Revenue Movement Incentive"
                  :tableData.sync="nonRevIncList"
                  :selectedRow.sync="selectedNonRevenueRows"
                />
              </div>
            </div>

            <div class="right-menu">
              <button :disabled="editMode.nonRevenue" @click="addRow('nonRevenue')">New</button>
              <transition name="details-expand">
                <BoxContainer
                  v-if="editMode.nonRevenue"
                  class="details-panel"
                  :title="formMode.nonRevenue == 'new' ? 'New Non-Revenue Details' : 'Edit Non-Revenue Details'"
                >
                  <div class="details-form">
                    <Input
                      placeholder="Movement Type"
                      :inputValue.sync="selectedNonRevenue['Movement']"
                      :isDisabled="formMode.nonRevenue == 'edit'"
                    />
                    <Input placeholder="Description" :inputValue.sync="selectedNonRevenue['Description']" />
                    <div class="details-rate-row">
                      <span>Driver</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedNonRevenue['Driver Rate($)']"
                        validator="postiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedNonRevenue['Driver Rate(%)']"
                        validator="postiveDecimal,11,2"
                      />
                      <div style="grid-column: 2 / -1; width: 75%">
                        <Dropdown
                          placeholder="From/To Tariff"
                          style="width: 100%"
                          :items="tariffDropdown"
                          itemKey="text"
                          :selectedObject.sync="selectedNonRevenueTariff"
                        />
                      </div>
                    </div>
                    <div class="details-rate-row">
                      <span>Attendant</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedNonRevenue['Attendant Rate($)']"
                        validator="postiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedNonRevenue['Attendant Rate(%)']"
                        validator="postiveDecimal,11,2"
                      />
                      <div style="grid-column: 2 / -1; width: 75%">
                        <Dropdown
                          placeholder="From/To Tariff"
                          :items="tariffDropdown"
                          itemKey="text"
                          :selectedObject.sync="selectedNonRevenueAttTariff"
                        />
                      </div>
                    </div>
                  </div>
                </BoxContainer>
              </transition>
              <div class="bottom-actions vertical">
                <template v-if="!editMode.nonRevenue">
                  <button @click="editNonRevenue" :disabled="selectedNonRevenueRows.length == 0"> Edit </button>

                  <button @click="deleteNonRevenue" :disabled="selectedNonRevenueRows.length == 0"> Delete </button>
                </template>

                <template v-else>
                  <button @click="saveNonRevenue">
                    {{ formMode.nonRevenue == 'new' ? 'Save New' : 'Save Edit' }}
                  </button>

                  <button @click="cancelNonRevenue"> Cancel </button>
                </template>
              </div>
            </div>
          </div>

          <div slot="Fixed-Zone Incentive" class="driver-tab-page split-view">
            <div class="list-side">
              <div class="tablebox-area">
                <TableBox
                  v-if="!tableLoading"
                  title="Fixed-Zone Incentive"
                  :tableData="fixedZoneIncentives"
                  :selectedRow.sync="selectedFixedZoneRows"
                />
              </div>
            </div>

            <div class="right-menu">
              <button :disabled="editMode.fixedZone" @click="addRow('fixedZone')">New</button>
              <transition name="details-expand">
                <BoxContainer
                  v-if="editMode.fixedZone"
                  class="details-panel"
                  :title="formMode.fixedZone == 'new' ? 'New Zone Details' : 'Edit Zone Details'"
                >
                  <div class="details-form">
                    <InputQuery
                      placeholder="Job Type"
                      :items="jobTypeOptions"
                      itemKey="Code - Description"
                      :selectedObject.sync="selectedFixedJobType"
                      :isDisabled="formMode.fixedZone == 'edit'"
                    />
                    <div class="zone-row">
                      <InputQuery
                        placeholder="From Zone"
                        :items="zoneList"
                        itemKey="Code - Description"
                        :selectedObject.sync="selectedFixedFromZone"
                        :isDisabled="formMode.fixedZone == 'edit'"
                      />
                    </div>
                    <div class="zone-row">
                      <InputQuery
                        placeholder="To Zone"
                        :items="zoneList"
                        itemKey="Code - Description"
                        :selectedObject.sync="selectedFixedToZone"
                        :isDisabled="formMode.fixedZone == 'edit'"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Driver</span>
                      <Input placeholder="$" :inputValue.sync="selectedFixedZone['Driver']" />
                      <span>+</span>
                      <Input placeholder="%" :inputValue.sync="selectedFixedZone['Driver Other Inc']" />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Attendant</span>
                      <Input placeholder="$" :inputValue.sync="selectedFixedZone['Attendant']" />
                      <span>+</span>
                      <Input placeholder="%" :inputValue.sync="selectedFixedZone['Attendant Other Inc']" />
                    </div>
                    <Input placeholder="B2B Coeff" :inputValue.sync="selectedFixedZone['B2B Coeff']" />
                  </div>
                </BoxContainer>
              </transition>
              <div class="bottom-actions vertical">
                <template v-if="!editMode.fixedZone">
                  <button @click="editFixedZone" :disabled="selectedFixedZoneRows.length == 0"> Edit </button>

                  <button @click="deleteFixedZone" :disabled="selectedFixedZoneRows.length == 0"> Delete </button>
                </template>

                <template v-else>
                  <button @click="saveFixedZone">
                    {{ formMode.fixedZone == 'new' ? 'Save New' : 'Save Edit' }}
                  </button>

                  <button @click="cancelFixedZone"> Cancel </button>
                </template>
              </div>
            </div>
          </div>

          <div slot="Add Incremental Incentive" class="driver-tab-page split-view">
            <div class="list-side">
              <div class="tablebox-area">
                <TableBox
                  v-if="!tableLoading"
                  title="Add Incremental Incentive"
                  :tableData="incrementalIncentives"
                  :selectedRow.sync="selectedIncrementalRows"
                  :hideColumns="['IncrementType']"
                />
              </div>
            </div>

            <div class="right-menu">
              <button :disabled="editMode.incremental" @click="addRow('incremental')">New</button>
              <transition name="details-expand">
                <BoxContainer
                  v-if="editMode.incremental"
                  class="details-panel"
                  :title="formMode.incremental == 'new' ? 'New Increment Details' : 'Edit Increment Details'"
                >
                  <div class="details-form">
                    <Input
                      placeholder="Trip From"
                      :inputValue.sync="selectedIncremental['Trip From']"
                      validator="positiveInteger"
                      :isDisabled="formMode.incremental == 'edit'"
                    />
                    <div class="details-rate-row compact-rate">
                      <span>Driver</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Rate($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Rate(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Attendant</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Attendant Rate($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Attendant Rate(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Drv Local</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Local($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Local(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Drv Medium</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Medium($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Medium(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Drv Outstn</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Outstation($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Outstation(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                  </div>
                  <RadioButtonSet
                    title="Type"
                    :buttons="incrementTypeOptions"
                    labelKey="label"
                    :selected.sync="selectedIncrementType"
                    :isHorizontal="true"
                    :showBox="false"
                    @radioChanged="incrementTypeChanged"
                  />
                </BoxContainer>
              </transition>
              <div class="bottom-actions vertical">
                <template v-if="!editMode.incremental">
                  <button @click="editIncremental" :disabled="selectedIncrementalRows.length == 0"> Edit </button>

                  <button @click="deleteIncremental" :disabled="selectedIncrementalRows.length == 0"> Delete </button>
                </template>

                <template v-else>
                  <button @click="saveIncremental">
                    {{ formMode.incremental == 'new' ? 'Save New' : 'Save Edit' }}
                  </button>

                  <button @click="cancelIncremental"> Cancel </button>
                </template>
              </div>
            </div>
          </div>
        </Tab>
      </div>

      <div v-if="activePage == 'specialDay'" class="page-content split-view">
        <div class="list-side">
          <div class="content-header">
            <div>Special Day Incentive</div>
          </div>
          <div class="top-selector">
            <InputQuery
              placeholder="Commission Code"
              :items="commissionList"
              itemKey="Code - Description"
              :selectedObject.sync="selectedSpecialDayCommission"
              :isDisabled="editMode.specialDay"
              @selected="specialDayCommissionSelected"
            />
          </div>
          <div class="tablebox-area">
            <TableBox
              v-if="!specialDayLoading"
              title="Special Day Incentive"
              :tableData="specialDayIncentives"
              :selectedRow.sync="selectedSpecialDayRows"
            />
          </div>
        </div>

        <div class="right-menu">
          <button :disabled="editMode.specialDay || !selectedSpecialDayCommission" @click="newSpecialDay"> New </button>

          <transition name="details-expand">
            <BoxContainer
              v-if="editMode.specialDay"
              class="details-panel"
              :title="formMode.specialDay == 'new' ? 'New Special Day Details' : 'Edit Special Day Details'"
            >
              <div class="field-grid one">
                <Dropdown
                  placeholder="Occurrence Type"
                  :items="occurrenceOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedOccurrenceType"
                  :isDisabled="formMode.specialDay == 'edit'"
                />

                <DateTimePicker
                  v-if="
                    selectedOccurrenceType &&
                    (selectedOccurrenceType.value == 'A' || selectedOccurrenceType.value == 'O')
                  "
                  placeholder="Date"
                  title="Date"
                  :showTime="false"
                  :value.sync="selectedSpecialDayDate"
                  :maxDate="new Date('2079-01-01')"
                />

                <Dropdown
                  v-if="selectedOccurrenceType && selectedOccurrenceType.value == 'M'"
                  placeholder="Day of Month"
                  :items="specialDayOfMonthOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedSpecialDayOfMonth"
                />

                <Dropdown
                  v-if="selectedOccurrenceType && selectedOccurrenceType.value == 'W'"
                  placeholder="Day of Week"
                  :items="specialDayOfWeekOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedSpecialDayOfWeek"
                />

                <Dropdown
                  placeholder="Remark"
                  :items="dayRemarkOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedDayRemark"
                  :search.sync="specialDayRemarkText"
                  :allowFreeText="true"
                />

                <RadioButtonSet
                  class="special-day-movement-options"
                  title="Movement"
                  :buttons="movementScopeOptions"
                  labelKey="label"
                  :selected.sync="selectedMovementScope"
                  :isHorizontal="true"
                  :showBox="false"
                  :isDisabled="formMode.specialDay == 'edit'"
                />

                <div v-if="selectedMovementScope && selectedMovementScope.value == 0" class="special-day-movement-type">
                  <InputQuery
                    placeholder="Movement Type"
                    :items="specialMovementTypeOptions"
                    itemKey="Code - Description"
                    :selectedObject.sync="selectedSpecialMovementType"
                    :isDisabled="formMode.specialDay == 'edit'"
                  />
                </div>

                <Dropdown
                  placeholder="Incentive Type"
                  :items="incentiveTypeOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedIncentiveType"
                />

                <Input
                  :placeholder="
                    selectedIncentiveType ? `Incentive Value (${selectedIncentiveType.label})` : 'Incentive Value'
                  "
                  :inputValue.sync="selectedSpecialDay['Incentive Value']"
                  validator="positiveDecimal,19,2"
                />

                <Dropdown
                  v-if="selectedIncentiveType && selectedIncentiveType.label == '%'"
                  placeholder="Base"
                  :items="baseOptions"
                  itemKey="label"
                  :selectedObject.sync="selectedBase"
                />

                <div
                  v-if="selectedOccurrenceType && selectedOccurrenceType.value == 'M'"
                  class="special-day-monthly-note"
                >
                  Note: Special Incentive will not be incurred for months that do not have the specified day.
                </div>
              </div>
            </BoxContainer>
          </transition>

          <div class="bottom-actions vertical">
            <template v-if="!editMode.specialDay">
              <button :disabled="selectedSpecialDayRows.length == 0" @click="editSpecialDay"> Edit </button>

              <button :disabled="selectedSpecialDayRows.length == 0" @click="deleteSpecialDay"> Delete </button>
            </template>

            <template v-else>
              <button @click="saveSpecialDay">
                {{ formMode.specialDay == 'new' ? 'Save New' : 'Save Edit' }}
              </button>

              <button @click="cancelSpecialDay"> Cancel </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  data: function () {
    return {
      activePage: 'driverIncentive',
      driverTabIndex: 0,
      selectedDriverCoeffMode: { label: 'No' },
      selectedNonRevenueRows: [],
      selectedFixedZoneRows: [],
      selectedIncrementalRows: [],
      selectedSpecialDayRows: [],
      editMode: {
        coefficientSettings: false,
        driverCommission: false,
        revenueMovement: false,
        specialDay: false,
        nonRevenue: false,
        fixedZone: false,
        incremental: false,
      },
      formMode: {
        driverCommission: '',
        revenueMovement: '',
        nonRevenue: '',
        fixedZone: '',
        incremental: '',
        specialDay: '',
      },
      menu: [
        {
          title: 'Coefficient Settings',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-todo-list-64.png',
          enabled: false,
          key: 'SSTab1',
        },
        {
          title: 'Driver Incentive',
          icon: 'https://dsmlib.logistics.systems/Icons/money.png',
          enabled: false,
          key: 'SSTab1_2',
        },
        {
          title: 'Special Day Incentive',
          icon: 'https://dsmlib.logistics.systems/Icons/Calendar.png',
          enabled: false,
          key: 'SSTab1_3',
        },
      ],
      driverIncentiveTabs: [
        { title: 'Driver Commission Structure' },
        { title: 'Revenue Movement Incentive' },
        { title: 'Non-Rev Mvt Inc (Fix)' },
        { title: 'Fixed-Zone Incentive' },
        { title: 'Add Incremental Incentive' },
      ],
      commissionList: [],
      driverCommissionList: [],
      jobTypeOptions: [],
      selectedFixedJobType: null,
      zoneList: [],
      selectedCommission: null,
      selectedDriverCommission: null,
      driverCommissionCode: '',
      driverCommissionDescription: '',
      selectedSpecialDayCommission: null,
      selectedNonRevenueTariff: null,
      selectedNonRevenueAttTariff: null,
      selectedIncrementType: { label: 'Gradual Increment', value: 0 },
      selectedOccurrenceType: { label: 'Annually', value: 'A' },
      selectedSpecialDayOfMonth: { label: '1', value: 1 },
      selectedSpecialDayOfWeek: { label: 'Sunday', value: 1 },
      selectedDayRemark: null,
      specialDayRemarkText: '',
      selectedSpecialMovementType: null,
      selectedMovementScope: { label: 'Select by Movement', value: 0 },
      selectedIncentiveType: { label: '%' },
      selectedBase: { label: 'of Driver Tariff Rate' },
      selectedSpecialDayDate: null,
      selectedMonthlyType: { label: '$' },
      selectedReserveType: { label: '%' },
      amountTypeOptions: [{ label: '$' }, { label: '%' }],
      tariffDropdown: [
        { value: 0, text: '-' },
        { value: 1, text: "of 'From' Tariff" },
        { value: 2, text: "of 'To' Tariff" },
        { value: 3, text: 'of Additional Billing' },
        { value: 4, text: 'of Driver Tariff Rate' },
        { value: 5, text: 'of Driver Fixed Zone Rate' },
      ],
      driverCoeffOptions: [{ label: 'No' }, { label: 'Yes' }, { label: '1 Way Special Additional' }],
      incrementTypeOptions: [
        { label: 'Gradual Increment', value: 0 },
        { label: 'Stage Increment', value: 1 },
      ],
      occurrenceOptions: [
        { label: 'Annually', value: 'A' },
        { label: 'Once', value: 'O' },
        { label: 'Monthly', value: 'M' },
        { label: 'Weekly', value: 'W' },
      ],
      specialDayOfMonthOptions: Array.from({ length: 31 }, (item, index) => ({
        label: String(index + 1),
        value: index + 1,
      })),
      specialDayOfWeekOptions: [
        { label: 'Sunday', value: 1 },
        { label: 'Monday', value: 2 },
        { label: 'Tuesday', value: 3 },
        { label: 'Wednesday', value: 4 },
        { label: 'Thursday', value: 5 },
        { label: 'Friday', value: 6 },
        { label: 'Saturday', value: 7 },
      ],
      dayRemarkOptions: [{ label: 'Labour Day' }, { label: 'Public Holiday' }, { label: 'Special Event' }],
      specialMovementTypeOptions: [],
      movementScopeOptions: [
        { label: 'Select by Movement', value: 0 },
        { label: 'All Movements', value: 1 },
        { label: 'All Rev Movements', value: 2 },
        { label: 'All non-Rev Movements', value: 3 },
        { label: 'All Fixed Zones', value: 4 },
      ],
      incentiveTypeOptions: [{ label: '%' }, { label: '$' }],
      baseOptions: [
        { label: 'of Driver Tariff Rate', value: 0 },
        { label: 'of Basic Incentive', value: 1 },
      ],
      movementTypes: ['DRD', 'D1W', 'C1W', 'DSS', 'DSE', 'CSS', 'CSE'],
      diversionTypes: ['DV1', 'DV2', 'DV3', 'DV4'],
      priceCoefficients: [],
      movementCoefficients: [],
      coefficientLoading: false,
      coefficientSettingsBackup: null,
      coefficientSettingsDetail: {},
      driverCommissionBackup: null,
      tableLoading: false,
      revMvtIncList: [],
      revMvtIncDet: [],
      diversionDet: [],
      revenueMovementBackup: null,
      nonRevIncList: [],
      selectedNonRevenue: {},
      fixedZoneIncentives: [],
      selectedFixedZone: {},
      selectedFixedFromZone: null,
      selectedFixedToZone: null,
      incrementalIncentives: [],
      selectedIncremental: {},
      specialDayIncentives: [],
      selectedSpecialDay: {},
      specialDayLoading: false,
    };
  },
  methods: {
    menuClicked: function (item) {
      switch (item.title) {
        case 'Coefficient Settings':
          this.activePage = 'coefficientSettings';
          break;

        case 'Driver Incentive':
          this.activePage = 'driverIncentive';
          break;

        case 'Special Day Incentive':
          this.activePage = 'specialDay';
          break;
      }
    },

    //#region coefficient settings
    mapPriceCoefficients: function (details) {
      const jobTypes = [
        { code: 'IMP', label: 'IMPORT' },
        { code: 'EXP', label: 'EXPORT' },
        { code: '1-IMP', label: '1-WAY IMPORT' },
        { code: '1-EXP', label: '1-WAY EXPORT' },
        { code: 'IMP-EXP', label: 'IMPORT-EXPORT' },
        { code: 'MTR', label: 'EMPTY-REJECT' },
        { code: 'REPO', label: 'REPOSITION' },
        { code: '1-IMP-MT', label: '1-WAY IMP (MT)' },
        { code: '1-EXP-MT', label: '1-WAY EXP (MT)' },
      ];

      return jobTypes.map((jobType, index) => {
        const data = (details || []).find((item) => {
          const code = String(item.JobTypeCode ?? item.JobType ?? item['Job Type'] ?? '').trim();

          return code == jobType.code;
        });

        return {
          '#': index + 1,
          JobTypeCode: jobType.code,
          'Job Type': jobType.label,
          'Price Coefficient': this.formatDecimalValue(
            data?.PriceCoefficient ?? data?.['Price Coefficient'] ?? data?.JobTypeCoefficient ?? '0',
            5,
          ),
        };
      });
    },
    mapMovementCoefficients: function (details) {
      const jobTypes = [
        { code: 'IMP', label: 'IMPORT' },
        { code: 'EXP', label: 'EXPORT' },
        { code: '1-IMP', label: '1-WAY IMPORT' },
        { code: '1-EXP', label: '1-WAY EXPORT' },
        { code: 'IMP-EXP', label: 'IMPORT-EXPORT' },
        { code: 'MTR', label: 'EMPTY-REJECT' },
        { code: 'REPO', label: 'REPOSITION' },
        { code: '1-IMP-MT', label: '1-WAY IMP (MT)' },
        { code: '1-EXP-MT', label: '1-WAY EXP (MT)' },
      ];

      const disabledCombinations = [
        {
          JobType: '1-IMP',
          MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'],
        },
        {
          JobType: '1-EXP',
          MovementTypes: ['DRD', 'D1W', 'DSS', 'DSE'],
        },
        {
          JobType: 'REPO',
          MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'],
        },
        {
          JobType: '1-IMP-MT',
          MovementTypes: ['DRD', 'D1W', 'DSS', 'DSE'],
        },
        {
          JobType: '1-EXP-MT',
          MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'],
        },
      ];

      const isDisabled = function (jobType, movementType) {
        return disabledCombinations.some((item) => {
          return item.JobType == jobType && item.MovementTypes.includes(movementType);
        });
      };

      return jobTypes.map((jobType) => {
        const row = {
          JobTypeCode: jobType.code,
          'Job Type': jobType.label,
        };

        this.movementTypes.forEach((movementType) => {
          const data = (details || []).find((item) => {
            const combinedKey = String(item.JTypeMvt ?? item.ControlTag ?? '').trim();
            const itemMovementType = String(item.MovementType ?? item['Movement Type'] ?? '').trim();
            const itemJobType = String(item.JobTypeCode ?? item.JobType ?? item['Job Type'] ?? '').trim();

            return (
              (itemJobType == jobType.code && itemMovementType == movementType) ||
              combinedKey == `${jobType.code}${movementType}`
            );
          });

          const disabled = isDisabled(jobType.code, movementType);

          row[movementType] = {
            value:
              disabled ? '' : (
                this.formatDecimalValue(
                  data?.MvtRateCoefficient ??
                    data?.MovementTypeCoefficient ??
                    data?.['Movement Type Coefficient'] ??
                    data?.Coefficient ??
                    data?.Coeff ??
                    data?.Value ??
                    '0',
                  5,
                )
              ),

            disabled: disabled,
            JobType: jobType.code,
            MovementType: movementType,
            key: data?.JTypeMvt || `${jobType.code}${movementType}`,
          };
        });

        return row;
      });
    },
    formatPriceCoefficient: function (row) {
      row['Price Coefficient'] = this.formatDecimalValue(row['Price Coefficient'], 5);
    },
    formatCoefficientCell: function (cell) {
      if (!cell || cell.disabled) {
        return;
      }

      cell.value = this.formatDecimalValue(cell.value, 5);
    },
    editCoefficientSettings: function () {
      this.coefficientSettingsBackup = {
        priceCoefficients: JSON.parse(JSON.stringify(this.priceCoefficients || [])),
        movementCoefficients: JSON.parse(JSON.stringify(this.movementCoefficients || [])),
        selectedDriverCoeffMode: JSON.parse(JSON.stringify(this.selectedDriverCoeffMode || {
              label: 'No',
            },
          ),
        ),
      };

      this.editMode.coefficientSettings = true;
    },

    cancelCoefficientSettings: function () {
      if (this.coefficientSettingsBackup) {
        this.priceCoefficients = JSON.parse(JSON.stringify(this.coefficientSettingsBackup.priceCoefficients || []));
        this.movementCoefficients = JSON.parse(JSON.stringify(this.coefficientSettingsBackup.movementCoefficients || []));

        this.selectedDriverCoeffMode = JSON.parse(JSON.stringify( this.coefficientSettingsBackup.selectedDriverCoeffMode || {
              label: 'No',
            },
          ),
        );
      }

      this.editMode.coefficientSettings = false;
      this.coefficientSettingsBackup = null;
    },

    buildMovementCoefficientPayload: function () {
      const payload = [];

      this.movementCoefficients.forEach((row) => { this.movementTypes.forEach((movementType) => {
        const cell = row[movementType];

        if (!cell || cell.disabled) {
          return;
        }

        payload.push({
          JobType: row.JobTypeCode,
          MovementType: movementType,
          JTypeMvt: cell.key || `${row.JobTypeCode}${movementType}`,
          Coefficient: this.formatDecimalValue(cell.value, 5),
        });
        });
      });

      return payload;
    },

    saveCoefficientSettings: async function () {
      const movementCoefficients = this.buildMovementCoefficientPayload();

      const priceCoefficients = this.priceCoefficients.map((item) => {
        return {
          jobtype: String(item.JobTypeCode || ''),
          value: this.formatDecimalValue(item['Price Coefficient'], 5),
        };
      });

      let driverCoeffMode = '0';

      if (this.selectedDriverCoeffMode?.label == 'Yes') {
        driverCoeffMode = '1';
      } else if (this.selectedDriverCoeffMode?.label == '1 Way Special Additional') {
        driverCoeffMode = '2';
      }

      const requestBody = {
        drivercoeffmode: driverCoeffMode,
        pricecoefficients: priceCoefficients,
        movementcoefficients: movementCoefficients.map((item) => {
          return {
            jobtype: String(item.JobType || ''),
            movementtype: String(item.MovementType || ''),
            key: String(item.JTypeMvt || ''),
            value: this.formatDecimalValue(item.Coefficient, 5),
          };
        }),
      };

      this.$fullscreenLoading('Updating Coefficient Settings');

      const result = await this.$apiCallV2('/Manage/SaveCoefficientSettings', requestBody);

      this.$fullscreenLoading();

      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);

        return;
      }

      this.editMode.coefficientSettings = false;
      this.coefficientSettingsBackup = null;

      this.$showAlert('success', 'Updated', result?.[0]?.['ErrMsg'] || 'Coefficient Settings updated successfully.');
    },

    //#region special day inc
    specialDayCommissionSelected: async function (selected) {
      if (!selected) {
        return;
      }

      const comCode = String(selected['Com Code'] || '').trim();

      if (!comCode) {
        return;
      }

      const normalCommission = this.commissionList.find((item) => {
        return String(item['Com Code'] || '').trim() == comCode;
      });

      if (!normalCommission) {
        return;
      }

      this.selectedSpecialDayCommission = normalCommission;

      await this.loadSpecialDayDetails(normalCommission);
    },
    loadSpecialDayDetails: async function (newVal) {
      if (!newVal) {
        return;
      }

      this.editMode.specialDay = false;
      this.formMode.specialDay = '';
      this.specialDayLoading = true;
      this.$fullscreenLoading('Populating List');

      this.specialDayIncentives = [];
      this.selectedSpecialDayRows = [];

      this.specialDayIncentives = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'GETSPECIALDAYINC',
        action: newVal['Com Code'],
      });

      this.$nextTick(() => {
        this.selectedSpecialDayRows = this.specialDayIncentives[0] ? [this.specialDayIncentives[0]] : [];
      });

      this.syncSelectedRows();

      this.specialDayLoading = false;
      this.$fullscreenLoading();
    },
    syncSpecialDayDetails: function () {
      const specialDay = this.selectedSpecialDay || {};
      const occurrenceCode = String(specialDay['Occur'] || '').trim();

      this.selectedOccurrenceType = this.occurrenceOptions.find((item) => {
        return item.value == occurrenceCode || item.label == occurrenceCode;
      }) || null;

      const remark = String(specialDay['Day remarks'] || '').trim();

      this.selectedDayRemark = this.dayRemarkOptions.find((item) => {
        return item.label == remark;
      }) || (remark ? { label: remark } : null);
      this.specialDayRemarkText = remark;

      const movementType = String(specialDay['MovementType'] || specialDay['Mvt Type'] || '').trim();
      const movementOption = Number(specialDay['Movement Option']);
      const isFixedZone = movementType.toUpperCase() == '@F@' || movementType.toUpperCase() == 'ALL FIXED ZONES';
      const movementScopeValue = isFixedZone ? 4 : [1, 2, 3].includes(movementOption) ? movementOption : 0;

      this.selectedMovementScope = this.movementScopeOptions.find((item) => {
        return item.value == movementScopeValue;
      }) || this.movementScopeOptions[0];
      this.selectedSpecialMovementType = isFixedZone ? null : (this.specialMovementTypeOptions.find((item) => {
          return String(item.MovementType || '').trim() == movementType;
        }) ||
        (movementType ?
          {
            MovementType: movementType,
            MovementTypeDesc: '',
            'Movement Type - Description': movementType,
          }
        : null)
      );

      const incentiveType = String(specialDay['Type'] || '').trim();

      this.selectedIncentiveType = this.incentiveTypeOptions.find((item) => {
        return item.label == incentiveType;
      }) || (incentiveType ? { label: incentiveType } : null);

      const base = String(specialDay['Base'] || '').trim();

      this.selectedBase = this.baseOptions.find((item) => {
        return item.label == base || String(item.value) == base;
      }) || (base ? { label: base } : null);

      const day = String(specialDay['Day'] || '').trim();
      if (day && (occurrenceCode == 'A' || occurrenceCode == 'O')) {
        this.selectedSpecialDayDate = new Date(day);
      } else if (occurrenceCode == 'M') {
        this.selectedSpecialDayOfMonth =
          this.specialDayOfMonthOptions.find((item) => {
            return String(item.value) == day;
          }) || this.specialDayOfMonthOptions[0];
      } else if (occurrenceCode == 'W') {
        this.selectedSpecialDayOfWeek =
          this.specialDayOfWeekOptions.find((item) => {
            return String(item.value) == day;
          }) || this.specialDayOfWeekOptions[0];
      }
    },
    newSpecialDay: function () {
      const comCode = String(this.selectedSpecialDayCommission?.['Com Code'] || '').trim();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');

        return;
      }

      this.selectedSpecialDay = {
        ID: '',
        'Commission Code': comCode,
        Occur: 'A',
        Day: '',
        'Day remarks': '',
        'Mvt Type': '',
        'Movement Scope': 'Select by Movement',
        'Movement Option': 0,
        'Incentive Value': '0.00',
        Type: '%',
        Base: 'of Driver Tariff Rate',
      };

      this.selectedOccurrenceType = {
        label: 'Annually',
        value: 'A',
      };
      this.selectedSpecialDayDate = new Date();
      this.selectedSpecialDayOfMonth = this.specialDayOfMonthOptions[0];
      this.selectedSpecialDayOfWeek = this.specialDayOfWeekOptions[0];
      this.selectedDayRemark = null;
      this.specialDayRemarkText = '';
      this.selectedSpecialMovementType = null;
      this.selectedMovementScope = this.movementScopeOptions.find((item) => {
        return item.label == 'Select by Movement';
      }) || null;

      this.selectedIncentiveType = this.incentiveTypeOptions.find((item) => {
        return item.label == '%';
      }) || null;

      this.selectedBase = this.baseOptions.find((item) => {
        return item.label == 'of Driver Tariff Rate';
      }) || null;

      this.formMode.specialDay = 'new';
      this.editMode.specialDay = true;
    },
    editSpecialDay: function () {
      if (!this.selectedSpecialDayRows || this.selectedSpecialDayRows.length == 0) {
        return;
      }

      this.selectedSpecialDay = JSON.parse(JSON.stringify(this.selectedSpecialDayRows[0]));

      this.syncSpecialDayDetails();

      this.formMode.specialDay = 'edit';
      this.editMode.specialDay = true;
    },
    cancelSpecialDay: function () {
      this.editMode.specialDay = false;
      this.formMode.specialDay = '';
      this.syncSelectedRows();
    },
    saveSpecialDay: async function () {
      const mode = this.formMode.specialDay;
      const comCode = String(this.selectedSpecialDayCommission?.['Com Code'] || '').trim();
      const occurrenceType = String(this.selectedOccurrenceType?.value || '').trim();
      const movementOption = Number(this.selectedMovementScope?.value ?? 0);
      const movementType = movementOption == 4 ? '@F@' : String(this.selectedSpecialMovementType?.MovementType || '').trim();
      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');
        return;
      }
      if (!occurrenceType) {
        this.$showAlert('warning', 'Occurrence Type Required', 'Please select an Occurrence Type.');
        return;
      }
      let day = String(this.selectedSpecialDay['Day'] || '').trim();
      if (occurrenceType == 'A' || occurrenceType == 'O') {
        day = this.selectedSpecialDayDate ? new Date(this.selectedSpecialDayDate).formatString('yyyy-MM-dd') : '';
      } else if (occurrenceType == 'M') {
        day = String(this.selectedSpecialDayOfMonth?.value || '');
      } else if (occurrenceType == 'W') {
        day = String(this.selectedSpecialDayOfWeek?.value || '');
      }
      if (!day) {
        this.$showAlert('warning', 'Day Required', 'Please select a date or day.');
        return;
      }
      if (movementOption == 0 && !movementType) {
        this.$showAlert('warning', 'Movement Type Required', 'Please select a Movement Type.');
        return;
      }
      if (!this.selectedIncentiveType) {
        this.$showAlert('warning', 'Incentive Type Required', 'Please select an Incentive Type.');
        return;
      }
      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        id: String(this.selectedSpecialDay['ID'] || this.selectedSpecialDay['SpecialDayRateID'] || '').trim(),
        comcode: comCode,
        occur: occurrenceType,
        day: day,
        remark: String(this.specialDayRemarkText || '').trim(),
        movementoption: String(movementOption),
        movementtype: movementType,
        incentivetype: String(this.selectedIncentiveType.label || '').trim(),
        incentivevalue: this.formatDecimalValue(this.selectedSpecialDay['Incentive Value'], 2),
        incentivebase: String(this.selectedBase?.value ?? 0),
      };
      this.$fullscreenLoading(mode == 'new' ? 'Creating Special Day Incentive' : 'Updating Special Day Incentive');
      const result = await this.$apiCallV2('/Manage/SaveSpecialDayInc', requestBody);
      this.$fullscreenLoading();
      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }
      const remarks = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'GETSPECIALDAY',
      });
      this.dayRemarkOptions = (remarks || []).map((item) => ({
        ...item,
        label: item.DateRemark,
      }));
      await this.loadSpecialDayDetails(this.selectedSpecialDayCommission);
      this.$showAlert(
        'success',
        mode == 'new' ? 'Created' : 'Updated',
        result?.[0]?.['ErrMsg'] ||
          (mode == 'new' ?
            'Special Day Incentive created successfully.'
          : 'Special Day Incentive updated successfully.'),
      );
    },
    deleteSpecialDay: async function () {
      if (!this.selectedSpecialDayRows || this.selectedSpecialDayRows.length == 0) {
        return;
      }
      const selectedRow = this.selectedSpecialDayRows[0];
      const id = String(selectedRow['ID'] || selectedRow['SpecialDayRateID'] || '').trim();
      if (!id) {
        this.$showAlert('warning', 'Delete Failed', 'Special Day Rate ID is missing.');
        return;
      }
      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Special Day Incentive',
        `Are you sure you want to delete Special Day Rate <b>${id}</b>?`,
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );
      if (confirmDelete == 'Cancel') {
        return;
      }
      this.$fullscreenLoading('Deleting Special Day Incentive');
      const result = await this.$apiCallV2('/Manage/SaveSpecialDayInc', { action: 'D', id: id });
      this.$fullscreenLoading();
      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);
        return;
      }
      await this.loadSpecialDayDetails(this.selectedSpecialDayCommission);
      this.$showAlert('success', 'Deleted', result?.[0]?.['ErrMsg'] || 'Special Day Incentive deleted successfully.');
    },
    //#endregion

    //#region driver incentive
    newDriverCommission: function () {
      this.driverCommissionBackup = {
        detail: JSON.parse(JSON.stringify(this.coefficientSettingsDetail || {})),
        selectedCommissionCode:
          this.selectedDriverCommission?.['Com Code'] || this.selectedCommission?.['Com Code'] || '',
        code: this.driverCommissionCode,
        description: this.driverCommissionDescription,
      };

      this.formMode.driverCommission = 'new';
      this.editMode.driverCommission = true;

      this.selectedDriverCommission = null;
      this.driverCommissionCode = '';
      this.driverCommissionDescription = '';

      const comHeader = {
        Com_Code: '',
        Remark: '',
        Basic_Pay1: '0.00',
        Basic_Pay2: '0.00',
        ComDetails: [],
      };

      this.coefficientSettingsDetail = this.mapCommissionStructure({
        ComHeader: comHeader,
      });

      this.coefficientSettingsDetail.monthlyBonus.Qlf = '$';
      this.coefficientSettingsDetail.reserve.Qlf = '%';

      this.selectedMonthlyType = { label: '$' };
      this.selectedReserveType = { label: '%' };
    },
    editDriverCommission: function () {
      if (!this.coefficientSettingsDetail.ComHeader) {
        return;
      }

      this.driverCommissionBackup = {
        detail: JSON.parse(JSON.stringify(this.coefficientSettingsDetail)),
        selectedCommissionCode: this.selectedCommission?.['Com Code'] || this.selectedDriverCommission?.['Com Code'] || '',
        code: this.driverCommissionCode,
        description: this.driverCommissionDescription,
      };

      this.formMode.driverCommission = 'edit';
      this.editMode.driverCommission = true;
    },
    cancelDriverCommission: function () {
      if (!this.driverCommissionBackup) {
        return;
      }

      const backup = this.driverCommissionBackup;

      this.coefficientSettingsDetail = JSON.parse(JSON.stringify(backup.detail || {}));
      this.editMode.driverCommission = false;
      this.formMode.driverCommission = '';

      this.syncCommissionSelectors(backup.selectedCommissionCode);

      this.driverCommissionCode = backup.code || backup.selectedCommissionCode || '';
      this.driverCommissionDescription = backup.description || '';

      if (this.coefficientSettingsDetail.monthlyBonus) {
        this.selectedMonthlyType = {
          label: this.coefficientSettingsDetail.monthlyBonus.Qlf || '$',
        };
      }

      if (this.coefficientSettingsDetail.reserve) {
        this.selectedReserveType = {
          label: this.coefficientSettingsDetail.reserve.Qlf || '%',
        };
      }

      this.driverCommissionBackup = null;
    },
    saveDriverCommission: async function () {
      const mode = this.formMode.driverCommission;
      const commissionCode = String(this.driverCommissionCode || '').trim();
      const description = String(this.driverCommissionDescription || '').trim();

      if (!commissionCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please enter a Commission Code.');
        return;
      }

      if (mode == 'new') {
        const duplicate = this.commissionList.some((item) => {
          return (
            String(item['Com Code'] || '').trim().toUpperCase() == commissionCode.toUpperCase()
          );
        });

        if (duplicate) {
          this.$showAlert('warning', 'Duplicate Commission Code', `Commission Code ${commissionCode} already exists.`);
          return;
        }
      }

      if (!description) {
        this.$showAlert('warning', 'Description Required', 'Please enter a Description.');
        return;
      }

      const commission = this.buildCommissionPayload();

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: commissionCode,
        remark: description,
        rangetype: String(commission.ComHeader.RangeType || 0),
        basicpay1: commission.ComHeader.Basic_Pay1,
        basicpay2: commission.ComHeader.Basic_Pay2,
        details: (commission.ComHeader.ComDetails || []).map((detail) => {
          return {
            comtype: String(detail.Com_Type || ''),
            seqno: String(detail.Seq_No || ''),
            range: detail.Range,
            incevery: detail.IncEvery,
            qlf: String(detail.Qlf || '$'),
            remarks: String(detail.Remarks || detail.Remark || ''),
            driver: detail.Driver,
            attendant: detail.Attendant,
          };
        }),
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Commission Structure' : 'Updating Commission Structure');

      const result = await this.$apiCallV2('/Manage/SaveDrvCom', requestBody);

      this.$fullscreenLoading();

      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }

      this.editMode.driverCommission = false;
      this.formMode.driverCommission = '';
      this.driverCommissionBackup = null;

      await this.refreshCommissionList(commissionCode);

      this.$showAlert(
        'success',
        mode == 'new' ? 'Created' : 'Updated',
        result?.[0]?.['ErrMsg'] ||
          (mode == 'new' ?
            `Commission ${commissionCode} created successfully.`
          : `Commission ${commissionCode} updated successfully.`),
      );
    },

    deleteDriverCommission: async function () {
      if (!this.coefficientSettingsDetail.ComHeader) {
        return;
      }

      const comCode = this.getSelectedCommissionCode();

      if (!comCode) {
        return;
      }

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Commission Structure',
        `Are you sure you want to delete Commission Code <b>${comCode}</b>?`,
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: comCode,
        remark: '',
        rangetype: '',
        basicpay1: '0.00',
        basicpay2: '0.00',
        details: [],
      };

      this.$fullscreenLoading('Deleting Commission Structure');

      const result = await this.$apiCallV2('/Manage/SaveDrvCom', requestBody);

      this.$fullscreenLoading();

      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);
        return;
      }

      await this.refreshCommissionList('');

      this.$showAlert(
        'success',
        'Commission Deleted',
        result?.[0]?.['ErrMsg'] || `Commission ${comCode} deleted successfully.`,
      );
    },

    editRevenueMovement: function () {
      if (!this.selectedCommission) {
        return;
      }

      this.revenueMovementBackup = {
        revMvtIncList: JSON.parse(JSON.stringify(this.revMvtIncList || [])),
        revMvtIncDet: JSON.parse(JSON.stringify(this.revMvtIncDet || [])),
        diversionDet: JSON.parse(JSON.stringify(this.diversionDet || [])),
      };

      this.formMode.revenueMovement = 'edit';
      this.editMode.revenueMovement = true;
    },

    cancelRevenueMovement: function () {
      if (this.revenueMovementBackup) {
        this.revMvtIncList = JSON.parse(JSON.stringify(this.revenueMovementBackup.revMvtIncList || []));
        this.revMvtIncDet = JSON.parse(JSON.stringify(this.revenueMovementBackup.revMvtIncDet || []));
        this.diversionDet = JSON.parse(JSON.stringify(this.revenueMovementBackup.diversionDet || []));
      }

      this.editMode.revenueMovement = false;
      this.formMode.revenueMovement = '';
      this.revenueMovementBackup = null;
    },

    saveRevenueMovement: async function () {
      const comCode = this.getSelectedCommissionCode();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');
        return;
      }

      const incentives = this.buildRevenueMovementPayload();
      const requestBody = {
        comcode: comCode,

        incentives: incentives.map((item) => {
          return {
            jobtype: String(item.JobType || ''),
            movementtype: String(item.MovementType || ''),
            key: String(item.JTypeMvt || ''),
            type: String(item.IncentiveType || '$'),
            value: String(item.IncentiveValue || '0'),
          };
        }),
      };

      this.$fullscreenLoading('Updating Revenue Movement Incentive');
      const result = await this.$apiCallV2('/Manage/SaveRevMvtInc', requestBody);
      this.$fullscreenLoading();

      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }

      this.editMode.revenueMovement = false;
      this.formMode.revenueMovement = '';
      this.revenueMovementBackup = null;

      const commission = this.commissionList.find((item) => {
        return String(item['Com Code'] || '').trim() == comCode;
      });

      if (commission) {
        await this.loadCommissionDetails(commission);
      }

      this.$showAlert(
        'success',
        'Updated',
        result?.[0]?.['ErrMsg'] || 'Revenue Movement Incentive updated successfully.',
      );
    },
    addRow: function (section) {
      this.formMode[section] = 'new';

      if (section == 'nonRevenue') {
        this.selectedNonRevenue = {};
        this.selectedNonRevenueTariff = null;
        this.selectedNonRevenueAttTariff = null;
      }

      if (section == 'fixedZone') {
        this.selectedFixedZone = {};
        this.selectedFixedJobType = null;
        this.selectedFixedFromZone = null;
        this.selectedFixedToZone = null;
      }

      if (section == 'incremental') {
        this.selectedIncremental = {};
        this.selectedIncrementType = { label: 'Gradual Increment', value: 0 };
      }

      this.editMode[section] = true;
    },
    editNonRevenue: function () {
      if (!this.selectedNonRevenueRows || this.selectedNonRevenueRows.length == 0) {
        return;
      }

      this.selectedNonRevenue = JSON.parse(JSON.stringify(this.selectedNonRevenueRows[0]));

      this.selectedNonRevenueTariff = this.tariffDropdown.find((item) => {
        return item.value == this.selectedNonRevenue['IncentivePercentType'];
      }) || null;

      this.selectedNonRevenueAttTariff = this.tariffDropdown.find((item) => {
        return item.value == this.selectedNonRevenue['AttnIncentivePercentType'];
      }) || null;

      this.formMode.nonRevenue = 'edit';
      this.editMode.nonRevenue = true;
    },
    cancelNonRevenue: function () {
      this.editMode.nonRevenue = false;
      this.formMode.nonRevenue = '';

      this.syncSelectedRows();

      const selectedRow = this.selectedNonRevenueRows[0];

      if (!selectedRow) {
        this.selectedNonRevenueTariff = null;
        this.selectedNonRevenueAttTariff = null;
        return;
      }

      this.selectedNonRevenueTariff = this.tariffDropdown.find((item) => {
        return item.value == selectedRow['IncentivePercentType'];
      }) || null;

      this.selectedNonRevenueAttTariff = this.tariffDropdown.find((item) => {
        return item.value == selectedRow['AttnIncentivePercentType'];
      }) || null;
    },

    saveNonRevenue: async function () {
      const mode = this.formMode.nonRevenue;
      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');
        return;
      }

      const movementType = String(this.selectedNonRevenue['Movement'] || '').trim();

      if (!movementType) {
        this.$showAlert('warning', 'Movement Type Required', 'Please enter a Movement Type.');
        return;
      }

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: comCode,
        movementtype: movementType,
        movementtypedesc: String(this.selectedNonRevenue['Description'] || '').trim(),
        driverfix: this.selectedNonRevenue['Driver Rate($)'],
        driverpercent: this.selectedNonRevenue['Driver Rate(%)'],
        attnfix: this.selectedNonRevenue['Attendant Rate($)'],
        attnpercent: this.selectedNonRevenue['Attendant Rate(%)'],
        driverpercenttype: String(this.selectedNonRevenueTariff?.value ?? 0),
        attnpercenttype: String(this.selectedNonRevenueAttTariff?.value ?? 0),
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Non-Revenue Incentive' : 'Updating Non-Revenue Incentive');

      const result = await this.$apiCallV2('/Manage/SaveNonRevInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }

      this.editMode.nonRevenue = false;
      this.formMode.nonRevenue = '';

      const commission = this.commissionList.find((item) => {
        return item['Com Code'] == comCode;
      });

      if (commission) {
        await this.loadCommissionDetails(commission);
      }

      this.$showAlert('success', mode == 'new' ? 'Created' : 'Updated', result[0]['ErrMsg']);
    },

    deleteNonRevenue: async function () {
      if (!this.selectedNonRevenueRows || this.selectedNonRevenueRows.length == 0) {
        return;
      }

      const selectedRow = this.selectedNonRevenueRows[0];
      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();
      const movementType = String(selectedRow['Movement'] || '').trim();

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Non-Revenue Incentive',
        `Are you sure you want to delete Movement Type <b>${movementType}</b>?`,
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: comCode,
        movementtype: movementType,
        movementtypedesc: '',
        driverfix: '0',
        driverpercent: '0',
        attnfix: '0',
        attnpercent: '0',
        driverpercenttype: '0',
        attnpercenttype: '0',
      };

      this.$fullscreenLoading('Deleting Non-Revenue Incentive');

      const result = await this.$apiCallV2('/Manage/SaveNonRevInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);
        return;
      }

      const commission = this.commissionList.find((item) => {
        return item['Com Code'] == comCode;
      });

      if (commission) {
        await this.loadCommissionDetails(commission);
      }

      this.$showAlert('success', 'Deleted', result[0]['ErrMsg']);
    },
    editFixedZone: function () {
      if (!this.selectedFixedZoneRows || this.selectedFixedZoneRows.length == 0) {
        return;
      }

      this.selectedFixedZone = JSON.parse(JSON.stringify(this.selectedFixedZoneRows[0]));

      this.selectedFixedJobType = this.jobTypeOptions.find((item) => {
        const jobType = item.JobType || item['Job Type'];
        const selectedJobType = this.selectedFixedZone.JobType || this.selectedFixedZone['Job Type'];
        return jobType == selectedJobType;
      }) || null;

      this.selectedFixedFromZone = this.zoneList.find((zone) => {
        return zone['Tariff Code'] == this.selectedFixedZone['From'];
      }) || null;

      this.selectedFixedToZone = this.zoneList.find((zone) => {
        return zone['Tariff Code'] == this.selectedFixedZone['To'];
      }) || null;

      this.formMode.fixedZone = 'edit';
      this.editMode.fixedZone = true;
    },

    cancelFixedZone: function () {
      this.editMode.fixedZone = false;
      this.formMode.fixedZone = '';

      this.syncSelectedRows();
    },

    saveFixedZone: async function () {
      const mode = this.formMode.fixedZone;

      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');
        return;
      }

      if (!this.selectedFixedJobType) {
        this.$showAlert('warning', 'Required Fields', 'Please select Job Type.');
        return;
      }

      if (!this.selectedFixedFromZone || !this.selectedFixedToZone) {
        this.$showAlert('warning', 'Zone Required', 'Please select From Zone and To Zone.');
        return;
      }

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: comCode,
        id: String(this.selectedFixedZone['ID'] || ''),
        jobtype: String(this.selectedFixedJobType['Job Type'] || ''),
        fromarea: String(this.selectedFixedFromZone['Tariff Code'] || ''),
        toarea: String(this.selectedFixedToZone['Tariff Code'] || ''),
        driverfixedincentive: this.selectedFixedZone['Driver'],
        attendantfixedincentive: this.selectedFixedZone['Attendant'],
        driverotherincentive: this.selectedFixedZone['Driver %'],
        attendantotherincentive: this.selectedFixedZone['Attendant %'],
        b2bcoeff: this.selectedFixedZone['B2B Coeff'],
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Fixed-Zone Incentive' : 'Updating Fixed-Zone Incentive');

      const result = await this.$apiCallV2('/Manage/SaveFixedZoneInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }

      this.editMode.fixedZone = false;
      this.formMode.fixedZone = '';

      const commission = this.commissionList.find((item) => {
        return item['Com Code'] == comCode;
      });

      if (commission) {
        await this.loadCommissionDetails(commission);
      }

      this.$showAlert('success', mode == 'new' ? 'Created' : 'Updated', result[0]['ErrMsg']);
    },

    deleteFixedZone: async function () {
      if (!this.selectedFixedZoneRows || this.selectedFixedZoneRows.length == 0) {
        return;
      }

      const selectedRow = this.selectedFixedZoneRows[0];

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Fixed-Zone Incentive',
        'Are you sure you want to delete this Fixed-Zone Incentive?',
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: this.selectedCommission['Com Code'],
        id: String(selectedRow['ID'] || ''),
        fromarea: '',
        toarea: '',
        driverfixedincentive: '0',
        attendantfixedincentive: '0',
        driverotherincentive: '0',
        attendantotherincentive: '0',
        b2bcoeff: '0',
      };

      this.$fullscreenLoading('Deleting Fixed-Zone Incentive');

      const result = await this.$apiCallV2('/Manage/SaveFixedZoneInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);
        return;
      }

      await this.loadCommissionDetails(this.selectedCommission);

      this.$showAlert('success', 'Deleted', result[0]['ErrMsg']);
    },
    editIncremental: function () {
      if (!this.selectedIncrementalRows || this.selectedIncrementalRows.length == 0) {
        return;
      }

      this.selectedIncremental = JSON.parse(JSON.stringify(this.selectedIncrementalRows[0]));

      this.selectedIncrementType = this.incrementTypeOptions.find((item) => {
        return item.label == this.selectedIncremental['IncrementType'];
      }) || this.incrementTypeOptions[0];

      this.formMode.incremental = 'edit';
      this.editMode.incremental = true;
    },

    cancelIncremental: function () {
      this.editMode.incremental = false;
      this.formMode.incremental = '';

      this.syncSelectedRows();
    },

    saveIncremental: async function () {
      const mode = this.formMode.incremental;

      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');
        return;
      }

      const tripFrom = String(this.selectedIncremental['Trip From'] || '').trim();

      if (!tripFrom) {
        this.$showAlert('warning', 'Trip From Required', 'Please enter Trip From.');
        return;
      }

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: comCode,
        incentivefix: this.selectedIncremental['Driver Rate($)'],
        incentivepercent: this.selectedIncremental['Driver Rate(%)'],
        attnincentivefix: this.selectedIncremental['Attendant Rate($)'],
        attnincentivepercent: this.selectedIncremental['Attendant Rate(%)'],
        incentivepercentlocal: this.selectedIncremental['Driver Local(%)'],
        incentivepercentmedium: this.selectedIncremental['Driver Medium(%)'],
        incentivepercentoutstation: this.selectedIncremental['Driver Outstation(%)'],
        incentivefixlocal: this.selectedIncremental['Driver Local($)'],
        incentivefixmedium: this.selectedIncremental['Driver Medium($)'],
        incentivefixoutstation: this.selectedIncremental['Driver Outstation($)'],
        triprangefrom: tripFrom,
        incrementtype: String(this.selectedIncrementType?.value ?? 0),
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Incremental Incentive' : 'Updating Incremental Incentive');

      const result = await this.$apiCallV2('/Manage/SaveAddIncrementalInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);
        return;
      }

      this.editMode.incremental = false;
      this.formMode.incremental = '';

      const commission = this.commissionList.find((item) => {
        return item['Com Code'] == comCode;
      });

      if (commission) {
        await this.loadCommissionDetails(commission);
      }

      this.$showAlert('success', mode == 'new' ? 'Created' : 'Updated', result[0]['ErrMsg']);
    },

    deleteIncremental: async function () {
      if (!this.selectedIncrementalRows || this.selectedIncrementalRows.length == 0) {
        return;
      }

      const selectedRow = this.selectedIncrementalRows[0];
      const tripFrom = String(selectedRow['Trip From'] || '').trim();

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Incremental Incentive',
        `Are you sure you want to delete Trip From <b>${tripFrom}</b>?`,
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: this.selectedCommission['Com Code'],
        incentivefix: '0',
        incentivepercent: '0',
        attnincentivefix: '0',
        attnincentivepercent: '0',
        incentivepercentlocal: '0',
        incentivepercentmedium: '0',
        incentivepercentoutstation: '0',
        incentivefixlocal: '0',
        incentivefixmedium: '0',
        incentivefixoutstation: '0',
        triprangefrom: tripFrom,
        incrementtype: String(selectedRow['IncrementType'] ?? this.selectedIncrementType?.value ?? 0),
      };

      this.$fullscreenLoading('Deleting Incremental Incentive');

      const result = await this.$apiCallV2('/Manage/SaveAddIncrementalInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);
        return;
      }

      await this.loadCommissionDetails(this.selectedCommission);

      this.$showAlert('success', 'Deleted', result[0]['ErrMsg']);
    },
    incrementTypeChanged: function (selected) {
      this.selectedIncrementType = selected;

      if (this.selectedIncremental) {
        this.selectedIncremental.IncrementType = selected ? selected.value : 0;
      }
    },
    amountTypeSelected: function (cell, selected) {
      const type = selected?.label || '$';

      cell.selectedType = selected;
      cell.type = type;

      const decimals = type == '%' ? 5 : 2;

      cell.value = this.formatDecimalValue(cell.value, decimals);

      if (type == '%') {
        cell.IncentivePercent = cell.value || '0.00000';
        cell.IncentiveFix = '0.00';
      } else {
        cell.IncentiveFix = cell.value || '0.00';
        cell.IncentivePercent = '0.00000';
      }
    },
    isLastMovement: function (movement) {
      return movement == this.movementTypes[this.movementTypes.length - 1];
    },
    mapRevMvtInc: function (revDetails) {
      const jobTypes = [
        { code: 'IMP', label: 'IMPORT' },
        { code: 'EXP', label: 'EXPORT' },
        { code: '1-IMP', label: '1-WAY IMPORT' },
        { code: '1-EXP', label: '1-WAY EXPORT' },
        { code: 'IMP-EXP', label: 'IMPORT-EXPORT' },
        { code: 'MTR', label: 'EMPTY-REJECT' },
        { code: 'REPO', label: 'REPOSITION' },
        { code: '1-IMP-MT', label: '1-WAY IMP (MT)' },
        { code: '1-EXP-MT', label: '1-WAY EXP (MT)' },
      ];

      const disabledCombinations = [
        { JobType: '1-IMP', MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'] },
        { JobType: '1-EXP', MovementTypes: ['DRD', 'D1W', 'DSS', 'DSE'] },
        { JobType: 'REPO', MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'] },
        { JobType: '1-IMP-MT', MovementTypes: ['DRD', 'D1W', 'DSS', 'DSE'] },
        { JobType: '1-EXP-MT', MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'] },
      ];

      const isFixedDisabled = function (jobType, movementType) {
        return disabledCombinations.some((item) => {
          return item.JobType == jobType && item.MovementTypes.includes(movementType);
        });
      };

      return jobTypes.map((jobType) => {
        const row = {
          JobTypeCode: jobType.code,
          'Job Type': jobType.label,
        };

        this.movementTypes.forEach((movementType) => {
          const data = revDetails.find((item) => {
            return (
              String(item.JobType || '').trim() == jobType.code &&
              String(item.MovementType || '').trim() == movementType
            );
          });

          const disabled = isFixedDisabled(jobType.code, movementType);
          const incentiveType = String(data?.IncentiveType || '$').trim() || '$';
          const incentiveValue = data?.IncentiveValue || '0.00';

          row[movementType] = {
            value: disabled ? '' : incentiveValue,
            type: incentiveType,
            selectedType: { label: incentiveType },
            disabled: disabled,
            JTypeMvt: data?.JTypeMvt || `${jobType.code}${movementType}`,
            JobType: jobType.code,
            MovementType: movementType,
            IncentiveFix: data?.IncentiveFix || '0.00',
            IncentivePercent: data?.IncentivePercent || '0.00000',
          };
        });

        return row;
      });
    },
    mapDiv: function (revDetails) {
      return this.diversionTypes.map((divType) => {
        const firstDiv = revDetails.find((item) => {
          return String(item.MovementType || '').trim() == divType;
        });

        const incentiveType = String(firstDiv?.IncentiveType || '$').trim() || '$';
        const incentiveValue = firstDiv?.IncentiveValue || (incentiveType == '%' ? '0.00000' : '0.00');

        return {
          key: divType,

          value: incentiveValue,

          type: incentiveType,

          selectedType: {
            label: incentiveType,
          },
        };
      });
    },
    //#endregion

    commissionSelected: async function (selected) {
      if (!selected) {
        return;
      }

      const comCode = String(selected['Com Code'] || '').trim();

      if (!comCode) {
        return;
      }

      const normalCommission = this.commissionList.find((item) => {
        return String(item['Com Code'] || '').trim() == comCode;
      });

      const driverCommission = this.driverCommissionList.find((item) => {
        return String(item['Com Code'] || '').trim() == comCode;
      });

      if (!normalCommission) {
        return;
      }

      this.selectedDriverCommission = driverCommission || null;
      this.driverCommissionCode = comCode;
      this.driverCommissionDescription = normalCommission['Remark'] || '';

      await this.loadCommissionDetails(normalCommission);
    },
    createCommissionDetail: function (comHeader, comType, seqNo) {
      return {
        Com_Code: comHeader.Com_Code || '',
        Com_Type: comType,
        Seq_No: seqNo,
        Range: '0',
        Driver: '0',
        Attendant: '0',
        Qlf: '$',
        IncEvery: '0',
      };
    },

    mapCommissionStructure: function (result) {
      const comHeader = result && result.ComHeader ? result.ComHeader : { ComDetails: [] };

      if (!Array.isArray(comHeader.ComDetails)) {
        comHeader.ComDetails = [];
      }

      let monthlyBonus = comHeader.ComDetails.find((detail) => {
        return String(detail.Com_Type || '').trim() == '3';
      });

      if (!monthlyBonus) {
        monthlyBonus = this.createCommissionDetail(comHeader, '3', '1');
        comHeader.ComDetails.push(monthlyBonus);
      }

      let reserve = comHeader.ComDetails.find((detail) => {
        return String(detail.Com_Type || '').trim() == '4';
      });

      if (!reserve) {
        reserve = this.createCommissionDetail(comHeader, '4', '2');
        comHeader.ComDetails.push(reserve);
      }

      return {
        ComHeader: comHeader,
        monthlyBonus: monthlyBonus,
        reserve: reserve,
      };
    },

    commissionTypeSelected: function (section, selected) {
      if (!this.coefficientSettingsDetail[section]) return;

      const type = selected?.label;

      this.coefficientSettingsDetail[section].Qlf = type;

      if (section == 'monthlyBonus') {
        this.selectedMonthlyType = { label: type };
      }

      if (section == 'reserve') {
        this.selectedReserveType = { label: type };
      }
    },

    buildCommissionPayload: function () {
      const comHeader = {
        ...this.coefficientSettingsDetail.ComHeader,
        Com_Code: String(this.driverCommissionCode || '').trim(),
        Remark: String(this.driverCommissionDescription || '').trim(),
      };

      return {
        ComHeader: comHeader,
      };
    },

    buildRevenueMovementPayload: function () {
      const payload = [];

      this.revMvtIncDet.forEach((row) => {
        this.movementTypes.forEach((movementType) => {
          const cell = row[movementType];

          if (!cell || cell.disabled) {
            return;
          }

          const type = this.getAmountType(cell) || '$';
          const value = this.formatDecimalValue(cell.value, type == '%' ? 5 : 2);

          payload.push({
            JTypeMvt: cell.JTypeMvt || `${row.JobTypeCode}${movementType}`,
            JobType: row.JobTypeCode,
            MovementType: movementType,
            IncentiveType: type,
            IncentiveValue: value,
            IncentiveFix: type == '$' ? value : '0.00',
            IncentivePercent: type == '%' ? value : '0.00000',
          });
        });
      });

      const diversionJobTypes = ['1-EXP', '1-EXP-MT', '1-IMP', 'EXP', 'IMP', 'IMP-EXP', 'MTR'];

      this.diversionDet.forEach((diversion) => {
        const type = this.getAmountType(diversion) || '$';
        const value = this.formatDecimalValue(diversion.value, type == '%' ? 5 : 2);

        diversionJobTypes.forEach((jobType) => {
          payload.push({
            JTypeMvt: `${jobType}${diversion.key}`,
            JobType: jobType,
            MovementType: diversion.key,
            IncentiveType: type,
            IncentiveValue: value,
            IncentiveFix: type == '$' ? value : '0.00',
            IncentivePercent: type == '%' ? value : '0.00000',
          });
        });
      });

      return payload;
    },

    driverCommissionSelected: async function (selected) {
      if (!selected) {
        return;
      }

      if (this.formMode.driverCommission == 'new') {
        return;
      }

      const comCode = String(selected['Com Code'] || '').trim();

      if (!comCode) {
        return;
      }

      const normalCommission = this.commissionList.find((item) => {
        return String(item['Com Code'] || '').trim() == comCode;
      });

      if (!normalCommission) {
        return;
      }

      this.selectedCommission = normalCommission;
      this.driverCommissionCode = comCode;
      this.driverCommissionDescription = normalCommission['Remark'] || '';

      await this.loadCommissionDetails(normalCommission);
    },

    refreshCommissionList: async function (comCode) {
      const result = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'LORRYCOMISSION',
      });

      this.commissionList = result.map((com) => ({
        ...com,
        'Code - Description': `${com['Com Code']} - ${com['Remark']}`,
      }));

      this.driverCommissionList = result.map((com) => ({
        ...com,
      }));

      const selectedCommission =
        this.commissionList.find((com) => {
          return String(com['Com Code'] || '').trim() == String(comCode || '').trim();
        }) ||
        this.commissionList[0] ||
        null;

      if (!selectedCommission) {
        this.selectedCommission = null;
        this.selectedDriverCommission = null;
        this.driverCommissionCode = '';
        this.driverCommissionDescription = '';
        this.coefficientSettingsDetail = {};
        return;
      }

      this.syncCommissionSelectors(selectedCommission['Com Code']);

      await this.loadCommissionDetails(selectedCommission);
    },

    getSelectedCommissionCode: function () {
      return String(
        this.selectedCommission?.['Com Code'] ||
          this.selectedDriverCommission?.['Com Code'] ||
          this.driverCommissionCode ||
          '',
      ).trim();
    },

    getAmountType: function (item) {
      return item?.selectedType?.label;
    },

    getAmountDecimals: function (item) {
      return this.getAmountType(item) == '%' ? 5 : 2;
    },

    getAmountValidator: function (item) {
      const decimals = this.getAmountDecimals(item);
      return `decimal,11,${decimals}`;
    },

    formatDecimalValue: function (value, decimals) {
      const rawValue = String(value || '')
        .replace(/,/g, '')
        .trim();

      if (rawValue === '') {
        return Number(0).toFixed(decimals);
      }

      const numberValue = Number(rawValue);

      if (Number.isNaN(numberValue)) {
        return Number(0).toFixed(decimals);
      }

      return numberValue.toFixed(decimals);
    },

    formatAmountCell: function (cell) {
      const decimals = this.getAmountDecimals(cell);
      cell.value = this.formatDecimalValue(cell.value, decimals);
    },

    syncCommissionSelectors: function (comCode) {
      const code = String(comCode || '').trim();

      if (!code) {
        this.selectedCommission = null;
        this.selectedDriverCommission = null;
        this.selectedSpecialDayCommission = null;
        this.driverCommissionCode = '';
        this.driverCommissionDescription = '';
        return;
      }

      const normalCommission =
        this.commissionList.find((item) => {
          return String(item['Com Code'] || '').trim() == code;
        }) || null;

      const driverCommission =
        this.driverCommissionList.find((item) => {
          return String(item['Com Code'] || '').trim() == code;
        }) || null;

      this.selectedCommission = normalCommission;
      this.selectedDriverCommission = driverCommission;
      this.selectedSpecialDayCommission = normalCommission;

      this.driverCommissionCode = code;

      this.driverCommissionDescription = normalCommission?.['Remark'] || driverCommission?.['Remark'] || '';
    },

    loadCommissionDetails: async function (newVal) {
      if (!newVal) {
        return;
      }

      this.editMode.driverCommission = false;
      this.formMode.driverCommission = '';
      this.driverCommissionBackup = null;

      this.editMode.revenueMovement = false;
      this.formMode.revenueMovement = '';
      this.revenueMovementBackup = null;

      this.tableLoading = true;
      this.$fullscreenLoading('Populating List');

      this.nonRevIncList = [];
      this.selectedNonRevenueRows = [];

      this.nonRevIncList = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'LOADFIXINC',
        action: newVal['Com Code'],
      });

      this.$nextTick(() => {
        this.selectedNonRevenueRows = this.nonRevIncList[0] ? [this.nonRevIncList[0]] : [];
      });

      const revDetails = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'GETINCENTIVECOEFF',
        action: newVal['Com Code'],
      });

      this.revMvtIncList = revDetails || [];

      this.revMvtIncDet = this.mapRevMvtInc(this.revMvtIncList);

      this.diversionDet = this.mapDiv(this.revMvtIncList);

      this.fixedZoneIncentives = [];
      this.selectedFixedZoneRows = [];

      this.fixedZoneIncentives = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'GETFIXEDZONEINCENTIVES',
        action: newVal['Com Code'],
      });

      this.$nextTick(() => {
        this.selectedFixedZoneRows = this.fixedZoneIncentives[0] ? [this.fixedZoneIncentives[0]] : [];
      });

      this.incrementalIncentives = [];
      this.selectedIncrementalRows = [];

      this.incrementalIncentives = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'GETINCREMENTALINCENTIVE',
        action: newVal['Com Code'],
      });

      this.$nextTick(() => {
        this.selectedIncrementalRows = this.incrementalIncentives[0] ? [this.incrementalIncentives[0]] : [];
      });

      const commissionDetails = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'GETCOMMISSIONDETAILS',

        recordid: newVal['Com Code'],
      });

      this.coefficientSettingsDetail = this.mapCommissionStructure(commissionDetails);

      this.selectedMonthlyType = {
        label: this.coefficientSettingsDetail.monthlyBonus.Qlf,
      };

      this.selectedReserveType = {
        label: this.coefficientSettingsDetail.reserve.Qlf,
      };

      this.syncSelectedRows();
      this.tableLoading = false;
      this.$fullscreenLoading();
    },

    syncSelectedRows: function () {
      this.selectedNonRevenue =
        this.selectedNonRevenueRows[0] ? JSON.parse(JSON.stringify(this.selectedNonRevenueRows[0])) : {};
      this.selectedFixedZone =
        this.selectedFixedZoneRows[0] ? JSON.parse(JSON.stringify(this.selectedFixedZoneRows[0])) : {};

      this.selectedFixedJobType =
        this.jobTypeOptions.find((item) => {
          return item['Job Type'] == this.selectedFixedZone['Job Type'];
        }) || null;

      this.selectedFixedFromZone =
        this.zoneList.find((zone) => {
          return zone['Tariff Code'] == this.selectedFixedZone['From'];
        }) || null;

      this.selectedFixedToZone =
        this.zoneList.find((zone) => {
          return zone['Tariff Code'] == this.selectedFixedZone['To'];
        }) || null;

      this.selectedIncremental =
        this.selectedIncrementalRows[0] ? JSON.parse(JSON.stringify(this.selectedIncrementalRows[0])) : {};

      this.selectedIncrementType =
        this.incrementTypeOptions.find((item) => {
          return String(item.value) == String(this.selectedIncremental['IncrementType']);
        }) || this.incrementTypeOptions[0];

      this.selectedSpecialDay =
        this.selectedSpecialDayRows[0] ? JSON.parse(JSON.stringify(this.selectedSpecialDayRows[0])) : {};
      this.syncSpecialDayDetails();
    },
  },
  async mounted() {
    this.$fullscreenLoading('Checking Security Access');
    let sasControl = [];
    sasControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=HMS&page=MNG`);

    this.menu = this.menu.map((menu) => ({
      ...menu,
      enabled: true,
    }));

    sasControl.forEach((s) => {
      const menu = this.menu.find((m) => m.key == s.Control);

      if (menu) {
        const property = String(s.Properties || '').toLowerCase();
        const value = String(s.PropertiesValue || '').toLowerCase();

        if (property == 'tabenabled') {
          menu.enabled = value !== 'false';
        } else {
          menu[property] = value !== 'false';
        }

        if (menu.enabled == false) {
          menu.message = 'Access Denied';
        }
      }
    });
    this.$fullscreenLoading();

    let mountedPromises = [];
    let promisesFulfilled = 0;
    this.coefficientLoading = true;
    this.specialDayLoading = true;

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'LORRYCOMISSION' }).then((result) => {
        this.commissionList = result.map((com) => ({
          ...com,
          'Code - Description': `${com['Com Code']} - ${com['Remark']}`,
        }));

        this.driverCommissionList = result.map((com) => ({
          ...com,
        }));
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GETCOEFFICIENTSETTINGS' }).then((result) => {
        this.priceCoefficients = this.mapPriceCoefficients(result || []);
        this.movementCoefficients = this.mapMovementCoefficients(result || []);
        this.coefficientLoading = false;

        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CHAMAREACODE' }).then((result) => {
        this.zoneList = result.map((zone) => ({
          ...zone,
          'Code - Description': `${zone['Tariff Code']} - ${zone['Area']}`,
        }));
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      Promise.all([
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'REVMVT' }),
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'NONREVMVT' }),
      ]).then(([revenueMovements, nonRevenueMovements]) => {
        const movementTypes = [...(revenueMovements || []), ...(nonRevenueMovements || [])];

        this.specialMovementTypeOptions = movementTypes.map((item) => ({
          ...item,
          'Code - Description': `${item.MovementType} - ${item.MovementTypeDesc}`,
        }));

        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GETSPECIALDAY' }).then((result) => {
        this.dayRemarkOptions = result.map((item) => ({
          ...item,
          label: item.DateRemark,
        }));

        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'JOBTYPE', module: 'HMS' }).then((result) => {
        this.jobTypeOptions = result.map((item) => ({
          ...item,
          'Code - Description': `${item['Job Type']} - ${item.Description}`,
        }));

        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    await Promise.allSettled(mountedPromises);
    this.coefficientLoading = false;

    const firstCommission = this.commissionList[0] || null;

    if (firstCommission) {
      this.syncCommissionSelectors(firstCommission['Com Code']);
      await this.loadCommissionDetails(firstCommission);
      await this.loadSpecialDayDetails(firstCommission);
    } else {
      this.specialDayLoading = false;
    }

    this.syncSelectedRows();
  },
  watch: {
    selectedNonRevenueRows: function () {
      this.syncSelectedRows();

      const selectedRow = this.selectedNonRevenueRows[0];

      if (!selectedRow) {
        this.selectedNonRevenueTariff = null;
        this.selectedNonRevenueAttTariff = null;
        return;
      }

      this.selectedNonRevenueTariff =
        this.tariffDropdown.find((i) => {
          return i.value == selectedRow['IncentivePercentType'];
        }) || null;

      this.selectedNonRevenueAttTariff =
        this.tariffDropdown.find((i) => {
          return i.value == selectedRow['AttnIncentivePercentType'];
        }) || null;
    },
    selectedFixedZoneRows: function () {
      this.syncSelectedRows();
    },
    selectedFixedFromZone: function (val) {
      if (this.selectedFixedZone && val) {
        this.selectedFixedZone['From'] = val['Tariff Code'];
      }
    },

    selectedFixedToZone: function (val) {
      if (this.selectedFixedZone && val) {
        this.selectedFixedZone['To'] = val['Tariff Code'];
      }
    },
    selectedIncrementalRows: function () {
      this.syncSelectedRows();
    },
    selectedSpecialDayRows: function () {
      this.syncSelectedRows();
    },
    selectedOccurrenceType: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Occur'] = val.value;
        if (val.value == 'M') {
          this.selectedSpecialDay['Day'] = String(this.selectedSpecialDayOfMonth?.value || 1);
        } else if (val.value == 'W') {
          this.selectedSpecialDay['Day'] = String(this.selectedSpecialDayOfWeek?.value || 1);
        }
      }
    },
    selectedSpecialDayOfMonth: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Day'] = String(val.value);
      }
    },
    selectedSpecialDayOfWeek: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Day'] = String(val.value);
      }
    },
    selectedDayRemark: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Day remarks'] = val.label;
      }
    },
    specialDayRemarkText: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay) {
        this.selectedSpecialDay['Day remarks'] = val;
      }
    },

    selectedSpecialMovementType: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay) {
        this.selectedSpecialDay['Mvt Type'] = val ? String(val.MovementType || '').trim() : '';
      }
    },

    selectedMovementScope: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Movement Scope'] = val.label;
        this.selectedSpecialDay['Movement Option'] = val.value;
        if (val.value == 4) {
          this.selectedSpecialDay['Mvt Type'] = '@F@';
        } else if (val.value == 0) {
          this.selectedSpecialDay['Mvt Type'] =
            this.selectedSpecialMovementType ? String(this.selectedSpecialMovementType.MovementType || '').trim() : '';
        }
      }
    },

    selectedIncentiveType: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Type'] = val.label;

        if (val.label == '%' && !this.selectedBase) {
          this.selectedBase = this.baseOptions[0];
        }
      }
    },

    selectedBase: function (val) {
      if (this.editMode.specialDay && this.selectedSpecialDay && val) {
        this.selectedSpecialDay['Base'] = val.label;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.manage-main {
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  font-size: 12px;
  display: flex;
  overflow: hidden;

  > .left-menu {
    display: flex;
    justify-content: center;
  }

  > .manage-workspace {
    width: 100%;
    margin-left: 5px;
    overflow: hidden;
    background: white;
    border-radius: 5px;
    padding: 5px;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  .coefficient-page {
    flex: 1;
    min-height: 0;
  }

  .coefficient-page .coefficient-top {
    display: grid;
    grid-template-columns: minmax(260px, 0.8fr) minmax(520px, 1.2fr);
    gap: 5px;
    flex: 1;
    min-height: 420px;
    max-height: none;
  }

  .coefficient-page .price-panel,
  .coefficient-page .movement-panel {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .coefficient-page .price-panel .table-wrap,
  .coefficient-page .movement-panel .table-wrap {
    min-height: 350px;
  }

  .coefficient-page > .bottom-actions {
    margin-top: auto;
  }

  .content-header {
    min-height: 34px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 8px;
    font-weight: bolder;
    border-bottom: 2px solid rgb(180, 180, 180);
  }

  .top-selector {
    width: 280px;
    min-height: 32px;

    &.driver-commission-selector {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 8px;

      .commission-code-input {
        width: 280px;
        flex-shrink: 0;
      }

      .commission-description-input {
        flex: 1;
        min-width: 0;
      }
    }
  }

  .driver-incentive-tab {
    flex: 1;
    min-height: 0;
  }

  .driver-tab-page {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    height: 100%;
    overflow: hidden;

    &.split-view {
      flex-direction: row;
    }
  }

  .coefficient-top {
    min-height: 290px;
    max-height: 45%;
    display: grid;
    grid-template-columns: minmax(260px, 0.8fr) minmax(520px, 1.2fr);
    gap: 5px;
  }

  .price-panel,
  .movement-panel,
  .coefficient-settings-bottom {
    min-height: 0;
  }

  .coefficient-settings-bottom {
    flex: 1.1;
  }

  .coefficient-settings-detail {
    gap: 8px;
  }

  .commission-loading {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #e9e9e9;
  }

  .fill-panel,
  .tablebox-area {
    flex: 1;
    min-height: 0;
  }

  .tablebox-area {
    overflow: hidden;
  }

  .split-view {
    flex-direction: row;
    min-height: 0;
  }

  .list-side {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    min-width: 0;
    flex: 1;
  }

  .right-menu {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    border: 1px solid #cbd3ff;
    border-radius: 5px;
    background-color: #f8f9ff;
    width: 300px;
    min-width: 300px;
    flex-shrink: 0;
    padding: 5px;
    overflow: hidden;

    ::v-deep .box-container-main {
      margin-top: 10px;
      background-color: #fff;
    }

    .bottom-actions.vertical {
      min-height: auto;
      padding: 0;
      border: none;
      background-color: transparent;

      button {
        width: 100%;
        min-height: 28px;
      }
    }
  }

  .details-panel {
    width: 100%;
    min-width: 0;
    flex: none;
  }

  .table-wrap {
    flex: 1;
    min-height: 0;
    overflow: auto;
    border: 1px solid #d8dcff;
    background: #fff;

    &.cipl-table-wrap {
      position: relative;
    }
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;

    .table-message-row {
      height: 100%;
    }

    .table-message-cell {
      height: calc(100vh - 280px);
      min-height: 300px;
      background-color: #e9e9e9;
      color: #000;
      text-align: center;
      vertical-align: top;
      padding-top: 8px;
      font-weight: normal;
      border: none;
    }

    th {
      background-color: #535cda;
      color: white;
      border: 1px solid #d8dcff;
      padding: 0 4px;
      height: 24px;
      min-height: 24px;
      text-align: center;
      white-space: nowrap;
      font-weight: 600;
      position: sticky;
      top: 0;
      z-index: 2;
    }

    td {
      border: 1px solid #d4d9ff;
      padding: 0 5px;
      height: 32px;
      min-height: 32px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    tbody tr:nth-child(odd) {
      background-color: #fff;
    }

    tbody tr:nth-child(even) {
      background-color: #e2e4ff;
    }

    tbody tr:hover {
      background-color: rgb(170, 170, 255);
    }

    input,
    select {
      width: 100%;
      min-width: 0;
      height: 24px;
      border: none;
      border-bottom: 1px solid #bfc3d6;
      background: transparent;
      font-size: 12px;
      outline: none;
    }

    input:disabled,
    select:disabled {
      color: #202020;
      opacity: 1;
    }

    &.incentive-grid {
      border-collapse: separate;
      border-spacing: 0;

      td {
        overflow: visible;
        position: relative;
        z-index: 1;
      }

      tbody tr:focus-within {
        position: relative;
        z-index: 30;
      }

      td:focus-within {
        z-index: 40;
      }
    }
  }

  .price-grid {
    th:first-child,
    td:first-child {
      width: 40px;
      text-align: center;
    }
  }

  .coefficient-grid,
  .incentive-grid {
    border-collapse: separate;
    border-spacing: 0;

    th {
      text-align: center;
    }

    td {
      overflow: visible;
      position: relative;
      z-index: 1;
    }

    tbody tr:focus-within {
      position: relative;
      z-index: 30;
    }

    td:focus-within {
      z-index: 40;
    }
  }

  .coefficient-grid {
    th:first-child,
    td:first-child {
      width: 145px;
    }
  }

  .amount-cell {
    display: grid;
    grid-template-columns: minmax(45px, 1fr) 56px;
    gap: 4px;
    align-items: center;
    position: relative;
    z-index: 1;

    &:focus-within {
      z-index: 1000;
    }
  }

  .field-grid {
    display: grid;
    gap: 5px;

    &.one {
      grid-template-columns: 1fr;
    }

    &.three {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    label {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    select {
      height: 28px;
      border: 1px solid #c8c8c8;
    }
  }

  .special-day-movement-options {
    ::v-deep .radio-buttons-container {
      flex-wrap: wrap;
      column-gap: 8px;
      row-gap: 2px;
    }
  }

  .special-day-movement-type {
    width: 100%;
    min-width: 0;
  }

  .special-day-monthly-note {
    color: #d60000;
    font-size: 10px;
    line-height: 1.25;
    white-space: normal;
    padding-top: 2px;
  }

  .details-form {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
  }

  .details-rate-row {
    display: grid;
    grid-template-columns: 70px minmax(60px, 1fr) 12px minmax(60px, 1fr) minmax(120px, 1.2fr);
    gap: 5px;
    align-items: center;

    > span {
      white-space: nowrap;
    }

    &.compact-rate {
      grid-template-columns: 85px minmax(60px, 1fr) 12px minmax(60px, 1fr);
    }
  }

  .zone-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 5px;
    width: 100%;
  }

  .formula-row {
    display: grid;
    grid-template-columns:
      auto
      minmax(100px, 1fr)
      auto
      minmax(100px, 1fr)
      minmax(100px, 1fr)
      auto
      minmax(100px, 1fr)
      auto
      minmax(100px, 1fr);

    align-items: end;
    gap: 5px;
    width: 100%;

    > * {
      min-width: 0;
    }

    > span {
      white-space: nowrap;
      align-self: center;
    }

    .formula-input,
    .formula-dropdown {
      width: 100%;
      min-width: 0;
    }
  }

  .diversion-row {
    display: flex;
    gap: 5px;
    margin-top: 5px;

    label {
      display: grid;
      grid-template-columns: 45px 95px 64px;
      align-items: center;
      gap: 3px;
    }
  }

  .bottom-actions {
    border: 1px solid #cbd3ff;
    border-radius: 5px;
    background-color: #f8f9ff;
    min-height: 34px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 5px;
    padding: 5px;

    &.vertical {
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
    }
  }

  button {
    border: 1px solid #9aa8ee;
    background-color: #aebcff;
    color: #1c2458;
    border-radius: 3px;
    padding: 4px 10px;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .details-expand-enter-active,
  .details-expand-leave-active {
    transition:
      max-height 0.3s ease,
      opacity 0.3s ease,
      transform 0.3s ease;
    overflow: hidden;
  }

  .details-expand-enter,
  .details-expand-leave-to {
    max-height: 0;
    opacity: 0;
    transform: scaleY(0);
    transform-origin: top;
  }

  .details-expand-enter-to,
  .details-expand-leave {
    max-height: 600px;
    opacity: 1;
    transform: scaleY(1);
    transform-origin: top;
  }
}
</style>
